import React, { useState, useEffect } from 'react';
import { Box, Typography, Fade, Zoom } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const LastSavedIndicator = ({ lastSaved }) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);
  const [previousLastSaved, setPreviousLastSaved] = useState(lastSaved);
  const [animationKey, setAnimationKey] = useState(0);

  // Trigger animation when lastSaved changes
  useEffect(() => {
    if (lastSaved) {
      const lastSavedTime =
        typeof lastSaved === 'string'
          ? lastSaved
          : new Date(lastSaved).getTime();
      const previousTime =
        typeof previousLastSaved === 'string'
          ? previousLastSaved
          : new Date(previousLastSaved || 0).getTime();

      // Always animate if there's a new save, even if timestamps are very close
      if (lastSavedTime !== previousTime || !previousLastSaved) {
        // Force animation restart by incrementing key and start spinning immediately
        setAnimationKey((prev) => prev + 1);
        setIsSpinning(true);
        setShowIndicator(true);

        // Stop spinning after animation
        const timer = setTimeout(() => {
          setIsSpinning(false);
        }, 600);

        setPreviousLastSaved(lastSaved);
        return () => clearTimeout(timer);
      } else {
        setShowIndicator(true);
      }
    } else {
      setShowIndicator(false);
    }
  }, [lastSaved]);

  const getRelativeSaveText = () => {
    if (!lastSaved) return null;

    const relTime = dayjs(lastSaved).fromNow();
    if (relTime === 'Invalid Date') return null;

    return (
      <div
        id="last-saved-text"
        style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
      >
        <SaveIcon
          key={animationKey}
          style={{
            fontSize: '12px',
            color: '#757575',
            transform: isSpinning ? 'rotate(360deg)' : 'rotate(0deg)',
            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        {`Saved ${relTime}`}
      </div>
    );
  };

  if (!lastSaved) return null;

  return (
    <Fade in={showIndicator} timeout={300}>
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: '#757575',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          zIndex: 10,
          opacity: 0.9,
          transition: 'opacity 0.3s ease',
          '&:hover': {
            opacity: 1,
          },
        }}
      >
        <Zoom
          in={showIndicator}
          timeout={200}
          style={{ transitionDelay: '100ms' }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{
              fontSize: '11px',
              lineHeight: 1.3,
              transition: 'color 0.3s ease',
            }}
          >
            {getRelativeSaveText()}
          </Typography>
        </Zoom>
      </Box>
    </Fade>
  );
};

export default LastSavedIndicator;
